import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  Button,
  Divider,
  CircularProgress,
  Alert,
  Grid,
  IconButton,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Edit as EditIcon,
  PlayArrow as ExecuteIcon,
  History as HistoryIcon,
} from '@mui/icons-material';
import { CodeEditor } from '../components/common/CodeEditor';
import { agentFunctionService } from '../services/agentFunctionService';
import { ROUTES } from '../utils/routes';
import type { AgentFunctionDetailDto, AgentFunctionRequestDto } from '../types/agentFunction';

const getStatusChip = (status: number) => {
  const map: Record<number, { label: string; color: 'default' | 'warning' | 'success' | 'error' }> = {
    0: { label: 'Pending', color: 'default' },
    1: { label: 'Processing', color: 'warning' },
    2: { label: 'Completed', color: 'success' },
    3: { label: 'Failed', color: 'error' },
  };
  const cfg = map[status] ?? { label: 'Unknown', color: 'default' };
  return <Chip label={cfg.label} color={cfg.color} size="small" />;
};

/**
 * Agent Function detail page showing definition, schemas, prompt, and recent executions.
 */
export const AgentFunctionDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [func, setFunc] = useState<AgentFunctionDetailDto | null>(null);
  const [recentRequests, setRecentRequests] = useState<AgentFunctionRequestDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    const loadData = async () => {
      setLoading(true);
      setError(null);
      try {
        const [detail, requests] = await Promise.all([
          agentFunctionService.getAgentFunction(id),
          agentFunctionService.getAgentFunctionRequests(id, { pageNumber: 1, pageSize: 5 }),
        ]);
        setFunc(detail);
        setRecentRequests(requests.items);
      } catch (err: any) {
        const msg = err.response?.data?.message || 'Failed to load agent function';
        setError(msg);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [id]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !func) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="error">{error || 'Agent function not found'}</Alert>
        <Button startIcon={<BackIcon />} onClick={() => navigate(ROUTES.AGENT_FUNCTIONS)} sx={{ mt: 2 }}>
          Back to Agent Functions
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 1200, mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <IconButton onClick={() => navigate(ROUTES.AGENT_FUNCTIONS)}>
          <BackIcon />
        </IconButton>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h5" fontWeight={600}>
            {func.name}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Slug: <strong>{func.slug}</strong>
          </Typography>
        </Box>
        <Chip
          label={func.isActive ? 'Active' : 'Inactive'}
          color={func.isActive ? 'success' : 'default'}
          variant={func.isActive ? 'filled' : 'outlined'}
        />
      </Box>

      {/* Action Buttons */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <Button
          variant="contained"
          color="primary"
          startIcon={<ExecuteIcon />}
          onClick={() => navigate(`${ROUTES.AGENT_FUNCTION_EXECUTE}?slug=${encodeURIComponent(func.slug)}`)}
        >
          Execute Function
        </Button>
        <Button
          variant="outlined"
          startIcon={<EditIcon />}
          onClick={() => navigate(ROUTES.AGENT_FUNCTION_EDIT.replace(':id', func.id))}
        >
          Edit
        </Button>
        <Button
          variant="outlined"
          startIcon={<HistoryIcon />}
          onClick={() => navigate(ROUTES.AGENT_FUNCTION_REQUESTS.replace(':id', func.id))}
        >
          View Execution History ({func.requestCount})
        </Button>
      </Box>

      {/* Overview */}
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ p: 4 }}>
          <Typography variant="h6" gutterBottom>
            Overview
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Typography variant="caption" color="text.secondary">
                Agent Definition
              </Typography>
              <Typography variant="body1">{func.agentDefinitionName}</Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Typography variant="caption" color="text.secondary">
                Version
              </Typography>
              <Typography variant="body1">{func.version}</Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Typography variant="caption" color="text.secondary">
                Created At
              </Typography>
              <Typography variant="body1">{new Date(func.createdAt).toLocaleString()}</Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Typography variant="caption" color="text.secondary">
                Updated At
              </Typography>
              <Typography variant="body1">{new Date(func.updatedAt).toLocaleString()}</Typography>
            </Grid>
            {func.description && (
              <Grid size={{ xs: 12 }}>
                <Typography variant="caption" color="text.secondary">
                  Description
                </Typography>
                <Typography variant="body1">{func.description}</Typography>
              </Grid>
            )}
          </Grid>
        </CardContent>
      </Card>

      {/* System Prompt */}
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ p: 4 }}>
          <Typography variant="h6" gutterBottom>
            System Prompt
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <CodeEditor
            value={func.systemPrompt}
            language="plaintext"
            height="250px"
            readOnly
          />
        </CardContent>
      </Card>

      {/* Schemas */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Card sx={{ height: '100%' }}>
            <CardContent sx={{ p: 4 }}>
              <Typography variant="h6" gutterBottom>
                Input Schema
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <CodeEditor
                value={func.inputSchemaJson}
                language="json"
                height="200px"
                readOnly
              />
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Card sx={{ height: '100%' }}>
            <CardContent sx={{ p: 4 }}>
              <Typography variant="h6" gutterBottom>
                Output Schema
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <CodeEditor
                value={func.outputSchemaJson}
                language="json"
                height="200px"
                readOnly
              />
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Recent Executions */}
      <Card>
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="h6">Recent Executions</Typography>
            {func.requestCount > 5 && (
              <Button
                size="small"
                onClick={() => navigate(ROUTES.AGENT_FUNCTION_REQUESTS.replace(':id', func.id))}
              >
                View All ({func.requestCount})
              </Button>
            )}
          </Box>
          <Divider sx={{ mb: 2 }} />
          {recentRequests.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
              No executions yet. Click "Execute Function" to run this agent function.
            </Typography>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {recentRequests.map((req) => (
                <Box
                  key={req.id}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    p: 1.5,
                    borderRadius: 1,
                    cursor: 'pointer',
                    '&:hover': { bgcolor: 'action.hover' },
                  }}
                  onClick={() =>
                    navigate(ROUTES.AGENT_FUNCTION_REQUEST_DETAIL.replace(':requestId', req.id))
                  }
                >
                  {getStatusChip(req.status)}
                  <Typography variant="body2" sx={{ flex: 1 }} noWrap>
                    {req.inputJson.length > 80
                      ? `${req.inputJson.substring(0, 80)}...`
                      : req.inputJson}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {req.durationMs != null ? `${req.durationMs}ms` : '—'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {new Date(req.createdAt).toLocaleString()}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};
